import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2, Paperclip, X } from "lucide-react";
import { API, api, formatApiError } from "../../lib/api";

const field = "w-full bg-transparent border-0 border-b border-[#0A192F]/25 px-0 py-3 text-[#0A192F] placeholder:text-[#6B7385] focus:outline-none focus:border-[#8A6A2B] transition-colors duration-300";
const label = "font-mono text-[11px] uppercase tracking-[0.22em] text-[#5A6578]";
const TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
const PURPOSES = ["Property purchase", "Bank loan", "Sale of property", "Legal / court matter", "Personal record", "Other"];

const FIELDS = [
  ["name", "Full name *", true], ["phone", "Mobile *", true], ["email", "Email", false],
  ["district", "District *", true], ["mandal", "Mandal *", true], ["village", "Village *", true],
  ["survey_no", "Survey no.", false], ["door_no", "Door no.", false], ["sro", "Sub-Registrar Office", false],
  ["document_no", "Document no.", false], ["document_year", "Document year", false],
];

const EMPTY = Object.fromEntries([...FIELDS.map(([k]) => [k, ""]), ["period_from", ""], ["period_to", ""], ["purpose", PURPOSES[0]], ["notes", ""]]);

const FileList = ({ files, onRemove }) => (
  <ul className="mt-3 space-y-2" data-testid="ec-file-list">
    {files.map((f, i) => (
      <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-3 rounded-xl bg-[#F4EEE3] px-4 py-2.5 text-sm text-[#0A192F]">
        <span className="truncate">{f.name} <span className="text-[#5A6578]">({(f.size / 1024 / 1024).toFixed(2)} MB)</span></span>
        <button type="button" onClick={() => onRemove(i)} className="text-[#5A6578] hover:text-red-700" data-testid={`ec-file-remove-${i}`} aria-label="Remove file"><X className="h-4 w-4" /></button>
      </li>
    ))}
  </ul>
);

export const ECRequestForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const inputRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const bad = picked.find((f) => !TYPES.includes(f.type) || f.size > 10 * 1024 * 1024);
    if (bad) toast.error(`${bad.name}: use PDF, JPG, PNG or WEBP under 10 MB`);
    const ok = picked.filter((f) => TYPES.includes(f.type) && f.size <= 10 * 1024 * 1024);
    setFiles((xs) => {
      const next = [...xs, ...ok];
      if (next.length > 5) toast.error("Up to 5 documents allowed");
      return next.slice(0, 5);
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.period_from && form.period_to && form.period_from > form.period_to) { toast.error("EC period 'from' must be before 'to'"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`${API}/ec-requests`, fd);
      setDone(data ?? {});
      toast.success("EC request received.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="py-12" data-testid="ec-success">
        <CheckCircle2 className="h-10 w-10 text-[#8A6A2B]" />
        <h3 className="mt-5 font-display text-4xl text-[#0A192F]">Request received, {form.name.split(" ")[0]}.</h3>
        <p className="mt-3 text-[#2C3545] max-w-md">We'll review the details for {form.village}, {form.mandal} and call you on {form.phone}.</p>
        {done?.ref && (
          <div className="mt-8 inline-flex flex-col rounded-2xl border border-[#8A6A2B]/40 bg-[#F4EEE3] px-6 py-4" data-testid="ec-success-ref">
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#5A6578]">Your reference number</span>
            <span className="mt-1 font-mono text-2xl tracking-[0.12em] text-[#0A192F]">{done.ref}</span>
            <span className="mt-2 text-sm text-[#4A5468]">Save it. Use it with your mobile number to <Link to="/track-ec" className="underline underline-offset-4 text-[#8A6A2B]" data-testid="ec-success-track-link">track your request</Link>.</span>
          </div>
        )}
        <br />
        <button onClick={() => { setDone(null); setForm(EMPTY); setFiles([]); }} className="mt-8 text-sm underline underline-offset-4 text-[#8A6A2B]" data-testid="ec-new-btn">Submit another request</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="ec-request-form">
      {FIELDS.map(([k, l, req]) => (
        <label key={k} className="block"><span className={label}>{l}</span>
          <input required={req} type={k === "email" ? "email" : "text"} inputMode={k === "phone" ? "tel" : k === "document_year" ? "numeric" : undefined}
            maxLength={k === "document_year" ? 4 : 200} value={form[k]} onChange={set(k)} className={field} data-testid={`ec-${k.replace("_", "-")}-input`} /></label>
      ))}
      <label className="block"><span className={label}>EC period from *</span>
        <input required type="date" value={form.period_from} onChange={set("period_from")} className={field} data-testid="ec-period-from-input" /></label>
      <label className="block"><span className={label}>EC period to *</span>
        <input required type="date" value={form.period_to} onChange={set("period_to")} className={field} data-testid="ec-period-to-input" /></label>
      <label className="block"><span className={label}>Purpose *</span>
        <select value={form.purpose} onChange={set("purpose")} className={`${field} cursor-pointer`} data-testid="ec-purpose-select">
          {PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select></label>
      <label className="block sm:col-span-2 lg:col-span-3"><span className={label}>Notes</span>
        <textarea rows={3} value={form.notes} onChange={set("notes")} className={`${field} resize-none`} placeholder="Anything else we should know about the property" data-testid="ec-notes-input" /></label>
      <div className="sm:col-span-2 lg:col-span-3">
        <span className={label}>Documents (optional): sale deed, old EC, survey sketch</span>
        <button type="button" onClick={() => inputRef.current?.click()} data-testid="ec-upload-btn"
          className="mt-3 flex w-full items-center justify-center gap-3 rounded-2xl border border-dashed border-[#8A6A2B]/60 bg-[#F4EEE3]/60 px-6 py-6 text-sm text-[#0A192F] transition-colors hover:bg-[#F4EEE3]">
          <Paperclip className="h-4 w-4 text-[#8A6A2B]" /> Attach files (PDF, JPG, PNG, WEBP, up to 5, 10 MB each)
        </button>
        <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={addFiles} className="hidden" data-testid="ec-file-input" />
        {files.length > 0 && <FileList files={files} onRemove={(i) => setFiles((xs) => xs.filter((_, j) => j !== i))} />}
      </div>
      <div className="sm:col-span-2 lg:col-span-3">
        <button type="submit" disabled={loading} data-testid="ec-submit-btn"
          className="group inline-flex items-center gap-4 rounded-full bg-[#0A192F] pl-8 pr-2 py-2 text-[#F4EEE3] transition-[background-color,transform] duration-300 hover:bg-[#13284A] active:scale-[0.98] disabled:opacity-60">
          <span className="text-sm tracking-wide">{loading ? "Uploading…" : "Submit EC request"}</span>
          <span className="grid h-11 w-11 place-items-center rounded-full bg-[#C5A059] text-[#070F1E] transition-transform duration-500 group-hover:rotate-[-45deg]">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          </span>
        </button>
      </div>
    </form>
  );
};
